import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AppStateFailureBuilder, AppStateFailureItemWithErrorCode, FailureType } from '@symphony-solo/shared/common-state/src';
import { ApiResponseCode } from '@symphony-solo/shared/http/src';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { VoucherService } from '../services';
import {
  getVoucherAction,
  getVoucherErrorAction,
  getVoucherSuccessAction,
  setVoucherAutoApplyAction,
  setVoucherAutoApplyErrorAction,
  setVoucherAutoApplySuccessAction,
} from './voucher.actions';
import { AuthenticationFacade } from '@symphony-solo/domain/authentication/src/lib/+state/authentication.facade';

@Injectable()
export class VoucherEffects {
  constructor(private action$: Actions, private voucherService: VoucherService, private authenticationFacade: AuthenticationFacade) {}

  protected getVoucher$ = createEffect(() =>
    this.action$.pipe(
      ofType(getVoucherAction),
      withLatestFrom(this.authenticationFacade.isAuthenticated$),
      switchMap(([action, isAuthenticated]) => {
        if (isAuthenticated) {
          return this.voucherService.getVouchers().pipe(
            map((voucher: any) => getVoucherSuccessAction({ voucher })),
            catchError(() => {
              const failure = new AppStateFailureBuilder()
                .setFailureType(FailureType.API)
                .addFailure(new AppStateFailureItemWithErrorCode('Error on getting vouchers', ApiResponseCode.Server_Error))
                .build();
              return of(getVoucherErrorAction({ failure }));
            })
          );
        }
        return of(getVoucherSuccessAction({ voucher: [] }));
      })
    )
  );

  protected setVoucherAutoApply$ = createEffect(() =>
    this.action$.pipe(
      ofType(setVoucherAutoApplyAction),
      switchMap((setVoucherAutoApply) => {
        return this.voucherService.setVoucherAutoApply(setVoucherAutoApply).pipe(
          map(() => setVoucherAutoApplySuccessAction()),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on setting vouchers', ApiResponseCode.Server_Error))
              .build();
            return of(setVoucherAutoApplyErrorAction({ failure }));
          })
        );
      })
    )
  );
}
